/** @format */

import {
	Center,
	Stack,
	Icon,
	SimpleGrid,
	Heading,
	Button,
	Text,
	Box,
	Tooltip,
	Show,
	Container,
} from '@chakra-ui/react';
import {InfoOutlineIcon} from '@chakra-ui/icons';

import {Formik, Form} from 'formik';
import {useEffect, useState} from 'react';
import {textStyles} from '../../../../styles/js/textStyles';
import {FormikField} from '../../../FormikField/FormikField';
import {MutationFormMessage} from '../../../MutationFormMessage';
import NextLink from 'next/link';
import * as Yup from 'yup';
import {htmlDecode} from '../../../../utils';
import axios from 'axios';
import YourDetailsRightImage from '../../../../../public/images/YourDetailsRight.webp';
import useUser from '../../../../../lib/useUser';
import {GraphQLError} from 'graphql';
import {User} from '../../../../pages/api/user';
import router from 'next/router';
import contentData from '../../../../data/contentData';
import {ImageProps} from 'next/image';
import ChakraNextImage from '../../../ChakraWrappedImage';

const YourDetailsLeft = ({nextStep}: {nextStep: () => void}) => {
	const {user} = useUser();
	const validationSchema = Yup.object().shape({
		firstName: Yup.string()
			.min(2, 'Too Short!')
			.max(50, 'Too Long!')
			.required('Required'),
		lastName: Yup.string()
			.min(2, 'Too Short!')
			.max(50, 'Too Long!')
			.required('Required'),
		businessName: Yup.string().min(2, 'Too Short!').max(50, 'Too Long!'),
		email: Yup.string().email('Invalid email').required('Required'),
		password: Yup.string()
			.required('Password is required.')
			.min(8, 'Passwords must be at least 8 characters in length.')
			.matches(
				/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{8,})/,
				'Passwords must be at least 8 characters and contain one number or symbol & at least one lower case & upper case character. Passwords are case sensitive.',
			),
		confirmPassword: Yup.string()
			.oneOf([Yup.ref('password')], 'Confirm Password must match Password')
			.required('Confirm Password is required'),
	});
	const initialValues: any = {
		firstName: '',
		lastName: '',
		businessName: '',
		email: '',
		password: '',
		confirmPassword: '',
		username: '',
	};

	const [messages, setMessages] = useState([]);
	const [isSubmitLoading, setSubmitLoading] = useState(false);

	const onSubmit = async values => {
		setMessages([]);
		setSubmitLoading(true);

		type RegisterUserInput = {
			graphQLErrors: GraphQLError[];
			registerUser: {
				user: User;
			};
		};

		try {
			const {memberPackage} = router.query;
			const redirectUrl = new URL('/members/login', window.location.origin);
			redirectUrl.searchParams.set('redirect', 'join');
			if (memberPackage) {
				redirectUrl.searchParams.set('memberPackage', memberPackage.toString());
			}

			const response = await axios.post<RegisterUserInput>(
				'/api/member/register',
				values,
			);

			if (response.data.registerUser.user) {
				await router.push(redirectUrl.toString());
			} else if (response.data.graphQLErrors.length) {
				const messageArray = response.data.graphQLErrors.map(item =>
					htmlDecode(item?.message),
				);
				setMessages(messageArray);
				setMessages(['Oops, something went wrong while registering member']);
			}

			setSubmitLoading(false);
		} catch (error: unknown) {
			setSubmitLoading(false);
			// console.error(error);
			setMessages(['It looks like that email address or username is already in use. Please choose a different email address or username to complete your account.']);
		}
	};

	useEffect(() => {
		if (user?.isLoggedIn) {
			nextStep();
		}
	}, [user]);

	return (
		<Center mb={6}>
			<SimpleGrid columns={1}>
				<Heading sx={textStyles.display5LeftDark}>
					Create your ASSC account
				</Heading>
				<Formik
					// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
					initialValues={initialValues}
					onSubmit={async values => onSubmit(values)}
					validationSchema={validationSchema}>
					{({errors, touched, isSubmitting}) => (
						<Box maxW='22.5rem'>
							<Form>
								<FormikField
									errors={errors}
									touched={touched}
									label={'First Name'}
									nameKey={'firstName'}
									type={'text'}
								/>
								<FormikField
									errors={errors}
									touched={touched}
									label={'Last Name'}
									nameKey={'lastName'}
									type={'text'}
								/>
								<FormikField
									errors={errors}
									touched={touched}
									label={'Business Name'}
									nameKey={'businessName'}
									type={'text'}
								/>
								<FormikField
									errors={errors}
									touched={touched}
									type={'email'}
									label={'Email'}
									nameKey={'email'}
									autoComplete='email'
								/>

								<FormikField
									errors={errors}
									touched={touched}
									label={'Username'}
									nameKey={'username'}
									tooltip={<UsernameTooltip />}
								/>

								<FormikField
									errors={errors}
									touched={touched}
									type={'password'}
									label={'Password'}
									nameKey={'password'}
									autoComplete={'new-password'}
								/>
								<Text sx={textStyles.paragraph2LefGrey}>
									Passwords must be at least 8 characters and contain one number or symbol & at least one lower case & upper case character. Passwords are case sensitive.
								</Text>
								<FormikField
									errors={errors}
									touched={touched}
									type={'password'}
									label={'Confirm Password'}
									nameKey={'confirmPassword'}
									autoComplete={'new-password'}
								/>
								<SimpleGrid columns={1} spacing={6} my={6}>
									<Box sx={textStyles.paragraph3LeftItalicDark}>
										{
											'By becoming a member of the ASSC, you are agreeing to adhere to the '
										}
										<Box
											sx={{
												textDecoration: 'underline',
											}}>
											<NextLink href={'/guidance-sheet/assc-committed-to-quality-scheme-ctq'}>
												ASSC Committed to Quality Scheme.
											</NextLink>
										</Box>
									</Box>
									<Text sx={textStyles.paragraph3LeftItalicDark}>
										ASSC and EmbraceScotland will use the information you
										provide on this form to keep in touch with you. If at any
										time you would like your details to be updated or removed,
										please get in touch.
									</Text>
									<Box sx={textStyles.paragraph3LeftItalicDark}>
										{'By creating an account, I agree to the '}
										<Box
											sx={{
												textDecoration: 'underline',
											}}>
											<NextLink href={'/terms-of-website-use'}>ASSC Terms and Conditions</NextLink>
										</Box>
										{' and '}
										<Box
											sx={{
												textDecoration: 'underline',
											}}>
											<NextLink href={'/data-privacy-notice'}>Privacy Statement.</NextLink>
										</Box>
									</Box>
								</SimpleGrid>
								<MutationFormMessage messages={messages} />
								<Button
									type='submit'
									disabled={isSubmitting || isSubmitLoading}
									isLoading={isSubmitLoading}>
									Submit
								</Button>
							</Form>
						</Box>
					)}
				</Formik>
			</SimpleGrid>
		</Center>
	);
};

const YourDetailsRight = () => {
	const imageProps: ImageProps = {
		src: YourDetailsRightImage.src,
		alt: 'coast',
	};
	return (
		<Box position='relative' alignItems={'flex-start'}>
			<ChakraNextImage
				image={imageProps}
				opacity={'1'}
				alt={'coast'}
				objectFit={'cover'}
			/>
		</Box>
	);
};

export {YourDetailsLeft, YourDetailsRight};

export const UsernameTooltip = () => (
	<>
		<Show above='md'>
			<Tooltip
				label={
					<Stack>
						{contentData.usernameOnRegister.tooltip.map((item, i) => (
							<Text key={`${i}-tooltip-text`}>{item}</Text>
						))}
					</Stack>
				}
				aria-label='A tooltip'>
				<Icon as={InfoOutlineIcon} />
			</Tooltip>
		</Show>
		<Show below='md'>
			<Stack sx={textStyles.paragraph2LeftGrey}>
				{contentData.usernameOnRegister.tooltip.map((item, i) => (
					<Text key={`${i}-tooltip-text`}>{item}</Text>
				))}
			</Stack>
		</Show>
	</>
);
